import { ArrowUp, Bot, Check, MessageSquare, Minimize2, PenLine, Sparkles, X } from 'lucide-react'
import { type FormEvent, type KeyboardEvent, useState } from 'react'

import type { DocumentRecord } from '../../shared/types'
import { ApiClientError, apiFetch } from '../api'
import { DiffPreview } from './DiffPreview'

export interface AiProposal {
  summary: string
  title: string
  content: string
  baseVersion: number
}

interface ChatItem { role: 'user' | 'assistant'; content: string }

export function AiAssistant({ document, canInteract, onApply, onClose }: { document: DocumentRecord; canInteract: boolean; onApply: (proposal: AiProposal) => Promise<void>; onClose: () => void }) {
  const [mode, setMode] = useState<'chat' | 'edit'>('chat')
  const [instruction, setInstruction] = useState('')
  const [chat, setChat] = useState<ChatItem[]>([])
  const [proposal, setProposal] = useState<AiProposal | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [applied, setApplied] = useState(false)

  async function submit(event: FormEvent) {
    event.preventDefault()
    if (!instruction.trim() || !canInteract) return
    setBusy(true)
    setError('')
    setApplied(false)
    const prompt = instruction.trim()
    try {
      if (mode === 'chat') {
        setChat((items) => [...items, { role: 'user', content: prompt }])
        const result = await apiFetch<{ reply: string; baseVersion: number }>('/api/ai/chat', {
          method: 'POST', body: JSON.stringify({ documentId: document.id, version: document.version, instruction: prompt }),
        })
        setChat((items) => [...items, { role: 'assistant', content: result.reply }])
        setInstruction('')
      } else {
        const result = await apiFetch<{ proposal: AiProposal }>('/api/ai/edit', {
          method: 'POST', body: JSON.stringify({ documentId: document.id, version: document.version, instruction: prompt }),
        })
        setProposal(result.proposal)
      }
    } catch (reason) {
      setError(reason instanceof ApiClientError ? reason.message : 'AI 暂时无法响应，请重试')
    } finally { setBusy(false) }
  }

  async function apply() {
    if (!proposal) return
    setBusy(true)
    setError('')
    try {
      await onApply(proposal)
      setProposal(null)
      setInstruction('')
      setApplied(true)
    } catch (reason) { setError(reason instanceof Error ? reason.message : '建议无法应用，请重试') }
    finally { setBusy(false) }
  }

  function selectMode(next: 'chat' | 'edit') {
    setMode(next)
    if (next === 'chat') setProposal(null)
  }

  function navigateTabs(event: KeyboardEvent<HTMLButtonElement>) {
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return
    event.preventDefault()
    const tabs = [...(event.currentTarget.parentElement?.querySelectorAll<HTMLButtonElement>('[role="tab"]') ?? [])]
    const current = tabs.indexOf(event.currentTarget)
    const index = event.key === 'Home' ? 0 : event.key === 'End' ? tabs.length - 1 : (current + (event.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length
    const tab = tabs[index]
    if (!tab) return
    tab.focus()
    selectMode(tab.dataset.mode as 'chat' | 'edit')
  }

  return (
    <aside className="side-panel ai-panel" aria-label="AI 文档助手">
      <header className="ai-heading"><div className="ai-orb"><Sparkles size={17} /></div><div><p className="eyebrow">GPT-5.6</p><h2>文档助手</h2></div><button type="button" onClick={onClose} aria-label="折叠 AI 助手"><Minimize2 size={18} /></button></header>
      <div className="ai-mode" role="tablist" aria-label="AI 模式"><button id="ai-chat-tab" data-mode="chat" type="button" role="tab" aria-selected={mode === 'chat'} aria-controls="ai-chat-panel" tabIndex={mode === 'chat' ? 0 : -1} onClick={() => selectMode('chat')} onKeyDown={navigateTabs}><MessageSquare size={15} />问答</button><button id="ai-edit-tab" data-mode="edit" type="button" role="tab" aria-selected={mode === 'edit'} aria-controls="ai-edit-panel" tabIndex={mode === 'edit' ? 0 : -1} onClick={() => selectMode('edit')} onKeyDown={navigateTabs} aria-label="修改"><PenLine size={15} />修改</button></div>
      <div className="ai-mode-panel" role="tabpanel" id={`ai-${mode}-panel`} aria-labelledby={`ai-${mode}-tab`}>
        {!canInteract ? <p className="ai-wait"><Bot size={16} />请等待当前修改保存后再调用 AI。</p> : null}
        {mode === 'chat' && chat.length ? <div className="chat-stream" aria-live="polite">{chat.map((item, index) => <div className={item.role} key={`${item.role}-${index}`}><span>{item.role === 'assistant' ? <Sparkles size={13} /> : '你'}</span><p>{item.content}</p></div>)}</div> : null}
        {mode === 'edit' && !proposal ? <div className="quick-actions"><p>常用操作</p>{['润色全文', '续写下一段', '压缩并提炼', '翻译成英文'].map((item) => <button type="button" key={item} onClick={() => setInstruction(item)}>{item}<ArrowUp size={13} /></button>)}</div> : null}
        {proposal ? <><DiffPreview document={document} proposal={proposal} /><div className="proposal-actions"><button className="ghost-button" type="button" onClick={() => setProposal(null)} aria-label="取消建议"><X size={15} />取消</button><button className="primary-button" type="button" onClick={() => void apply()} disabled={busy}><Check size={15} />应用到文档</button></div></> : null}
        {applied ? <p className="ai-success" role="status"><Check size={14} />建议已作为新版本保存</p> : null}
        {error ? <p className="panel-error" role="alert">{error}</p> : null}
        {!proposal ? <form className="ai-compose" onSubmit={submit}><label htmlFor="ai-instruction" className="sr-only">给 AI 的指令</label><textarea id="ai-instruction" aria-label="给 AI 的指令" value={instruction} onChange={(event) => setInstruction(event.target.value)} placeholder={mode === 'chat' ? '询问这份文档…' : '描述希望怎样修改…'} /><button type="submit" disabled={!instruction.trim() || !canInteract || busy} aria-label={mode === 'chat' ? '发送给 AI' : '生成修改建议'}><ArrowUp size={18} /></button></form> : null}
        <p className="ai-disclaimer">AI 可能出错。修改应用前请检查预览。</p>
      </div>
    </aside>
  )
}
